'use client';
import OtpInput from 'react-otp-input';
import customerService from '@/_service/customer.service';
import { Box, Button, Center, Input, Stack, Text } from '@chakra-ui/react';
import { useTranslations } from 'next-intl';
import { useState } from 'react';
import Countdown from '../../../Countdown';
import { LuRotateCw } from 'react-icons/lu';
import { formatPhoneNumber } from '@/_utils/formatPhoneNumber';
import { FEATURED_BRANDS } from '@/_utils/Constants';

const OtpDesktop = ({
  isError,
  isSuccess,
  phoneNumber,
  backToLogin,
  onSubmit,
}: {
  isError: boolean;
  isSuccess: boolean;
  phoneNumber: string;
  backToLogin: () => void;
  onSubmit: (data: any) => void;
}) => {
  const [code, setCode] = useState('');
  const [isCountdown, setCountdown] = useState(true);

  const t = useTranslations();

  const onCountReset = () => {
    setCountdown(true);

    customerService
      .login({ phone: phoneNumber }, FEATURED_BRANDS.yaponamama)
      .then(() => {
        console.log({
          title: 'success.code_sent',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      })
      .catch(() => {
        console.log({
          title: t('errors.something_went_wrong'),
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      });

    setTimeout(() => {
      setCountdown(false);
    }, 60000);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (code.length === 6) {
      onSubmit(code);
    }
  };

  return (
    <Stack
      as='form'
      gap={5}
      style={{ flex: 1 }}
      position={'relative'}
      onSubmit={handleSubmit}
    >
      <Stack flexDir={'column'} gap={1}>
        <Text
          fontSize='2xl'
          fontWeight={700}
          lineHeight={'28px'}
          color={'text'}
        >
          {t('otp_enter_code_title')}
        </Text>
        <Stack gap={0}>
          <Text fontSize='sm' color='gray.800' lineHeight='20px'>
            {t('code_sent_to_number', {
              phoneNumber: formatPhoneNumber(String(phoneNumber)),
            })}
          </Text>
          <Text
            color='primary'
            cursor={'pointer'}
            fontWeight={500}
            onClick={backToLogin}
          >
            {t('change_number')}
          </Text>
        </Stack>
      </Stack>
      <Stack gap={'10px'} align='center'>
        <Center height='84px' flexDir='column'>
          <OtpInput
            value={code}
            onChange={(value) => {
              const sanitizedValue = value.replace(/[^0-9]/g, '');
              setCode(sanitizedValue);
            }}
            inputType='tel'
            containerStyle={{
              display: 'flex',
              gap: '8px',
            }}
            numInputs={6}
            shouldAutoFocus
            inputStyle={{
              width: '46px',
              height: '56px',
              fontSize: '20px',
              borderRadius: '8px',
              color: 'var(--text-default)',
              backgroundColor: 'var(--bg-default)',
              border: isError
                ? '1px solid red'
                : isSuccess
                  ? '1px solid green'
                  : undefined,
              textAlign: 'center',
            }}
            renderInput={(props) => <Input variant={'subtle'} {...props} />}
          />

          {isError && (
            <Text color='red.solid' fontWeight={500} mt={2}>
              {t('invalid_code_entered')}
            </Text>
          )}
        </Center>
        {isCountdown && (
          <Center
            mb={4}
            maxW={'250px'}
            flexDirection={'column'}
            justifyContent={'center'}
          >
            <Text color={'dark.900'} fontSize={'md'} textAlign={'center'}>
              {t('send_another_code')}{' '}
              <Countdown value={60} onTimeout={() => setCountdown(false)} />
            </Text>
          </Center>
        )}
      </Stack>
      <Box mt={2}>
        {!isCountdown && (
          <Button
            size='xl'
            variant='outline'
            w='full'
            mb={3}
            height='56px'
            rounded={16}
            fontWeight={600}
            textTransform='none'
            onClick={() => !isCountdown && onCountReset()}
          >
            <Text as={'span'} color={'primary'}>
              <LuRotateCw />
            </Text>
            {t('resend')}
          </Button>
        )}
        <Button
          type='submit'
          size='xl'
          w='full'
          height='56px'
          fontWeight={600}
          fontSize='md'
          bg='primary'
          color='primary.text'
          _hover={{
            bg: 'primary.hover',
          }}
          rounded={16}
          disabled={code.length < 6}
        >
          {t('confirm')}
        </Button>
      </Box>
    </Stack>
  );
};

export default OtpDesktop;
